/**
 * #950: a reload asks the server what changed, instead of throwing the saved
 * copy away.
 *
 * HQ draws itself from the copy it kept on the device (24 h) and then, until
 * now, refetched every query on screen (RefreshAfterRestore): a reload cost as
 * much as a cold start. The live stream can already answer "what changed since
 * T" from the audit log (#827), which survives an API restart. So, on a
 * restore:
 *
 *   1. `beginRestoreCheck` notes the oldest moment any restored answer is from
 *      (the check must cover every one of them) and holds the restored answers
 *      as current, so mounting a screen does not refetch it on age alone.
 *   2. The stream's first connection takes that moment (`takeRestoreCheck`)
 *      and asks `?since=`. Its hello says nothing changed (nothing to do),
 *      which areas changed (those are refetched, with every screen the server
 *      does not vouch for), or cannot say (everything is refetched).
 *   3. If no stream takes it (signed out, no stream on this page) or no hello
 *      arrives, the caller's fallback refetches everything: never worse than
 *      before.
 *
 * Only answers that were current when saved are held and asked about. One
 * already marked stale when saved (`isInvalidated`: a tab's stream had said
 * so) is left as it is, and refetches when a screen shows it, as before. (It
 * used to make the whole restore a plain refetch; the cache is shared by every
 * HQ tab, so one tab's invalidated, unmounted query nearly always defeated the
 * check, which the #950 walk found.) An answer past the server's horizon still
 * makes the whole restore a plain refetch.
 */
import type { QueryClient } from '@tanstack/react-query';

/** Past this the server answers "everything" anyway (changes-since.ts CHANGE_HORIZON_MS). */
export const RESTORE_CHECK_HORIZON_MS = 24 * 60 * 60 * 1000;

const pending = new WeakMap<QueryClient, string>();
// Clients whose restore began a check (whether or not a stream has taken it).
const started = new WeakSet<QueryClient>();

/**
 * Start the check for a just-restored cache. Returns the `since` the stream
 * should ask with, or null when the restore should be a plain refetch.
 */
export function beginRestoreCheck(
  queryClient: QueryClient,
  now: number = Date.now(),
): string | null {
  const restored = queryClient
    .getQueryCache()
    .getAll()
    .filter(
      (q) => q.state.status === 'success' && q.state.dataUpdatedAt > 0 && !q.state.isInvalidated,
    );
  if (restored.length === 0) return null;
  const oldest = Math.min(...restored.map((q) => q.state.dataUpdatedAt));
  if (now - oldest > RESTORE_CHECK_HORIZON_MS) return null;
  for (const q of restored) q.setState({ dataUpdatedAt: now });
  const since = new Date(oldest).toISOString();
  pending.set(queryClient, since);
  started.add(queryClient);
  return since;
}

/** The stream's first connection takes the moment to ask about (once). */
export function takeRestoreCheck(queryClient: QueryClient): string | null {
  const since = pending.get(queryClient) ?? null;
  pending.delete(queryClient);
  return since;
}

/**
 * A stream that closed before its hello hands the check back, for its next
 * connection (or the caller's fallback) to take.
 */
export function giveBackRestoreCheck(queryClient: QueryClient, since: string): void {
  if (!pending.has(queryClient)) pending.set(queryClient, since);
}

/**
 * Whether this restore began a check at all. A stream may already have taken
 * it by the time the caller's fallback mounts (the #950 walk: the stream opens
 * as soon as the restored "who am I" is there), so `restoreCheckWaiting` alone
 * cannot tell "no check" from "check under way".
 */
export function restoreCheckStarted(queryClient: QueryClient): boolean {
  return started.has(queryClient);
}

/** Whether a check is waiting for a stream to take it. */
export function restoreCheckWaiting(queryClient: QueryClient): boolean {
  return pending.has(queryClient);
}
